'use client';

import { useEffect, useState } from 'react';
import { PortableText } from '@portabletext/react';

import { GroqImage } from '@/helpers/Image';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/shadcn/ui/carousel';
import type { PagePatrickAffiliateSchema } from '@/sanity/pages/PagePatrickAffiliateTemplate';
import { measuredRichText } from './richtext';
import ChevronCta from './ChevronCta';

import waveDots from '@/public/fabio-affiliate/wave-dots.png';

/**
 * "Real Traders. Real Payouts." — Figma 8841:2178 (desktop) / 8811:2806
 * (mobile). Each slide pairs a quote with the trader's payout certificate:
 * side by side on desktop, stacked on mobile. The active slide's name reads at
 * full strength, the peeking neighbours at 50%.
 */
function Chevron({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden className={className}>
      <path
        d="M16.5 12L9.00002 19.5L7.95002 18.45L14.4 12L7.95002 5.55L9.00002 4.5L16.5 12Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function TradersSaySection({
  data,
}: {
  data: PagePatrickAffiliateSchema;
}) {
  const items = data?.tradersSayItems ?? [];
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    const sync = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    };
    sync();
    api.on('select', sync);
    api.on('reInit', sync);
    return () => {
      api.off('select', sync);
      api.off('reInit', sync);
    };
  }, [api]);

  if (
    !data?.tradersSayChip &&
    !data?.tradersSayHeading?.length &&
    !items.length &&
    !data?.tradersSayCTA
  )
    return null;

  const progress = count > 0 ? ((current + 1) / count) * 100 : 0;

  return (
    <section className="relative overflow-hidden bg-[#B4B4B4]/[15%] pt-[7.125rem] pb-[8rem] lg:pt-[13.875rem] lg:pb-[10.125rem]">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.09] mix-blend-exclusion pointer-events-none"
        style={{ backgroundImage: `url(${waveDots.src})`, backgroundSize: 'cover' }}
      />

      <div className="flex relative flex-col items-center w-full gap-[4rem] lg:gap-[6rem]">
        {(data?.tradersSayChip || data?.tradersSayHeading?.length > 0) && (
          <div className="flex flex-col items-center px-[2rem] gap-[1rem]">
            {data?.tradersSayChip && (
              <p className="m-0 italic text-center text-black font-blacker text-[2rem] leading-[1.5]">
                {data.tradersSayChip}
              </p>
            )}
            {data?.tradersSayHeading?.length > 0 && (
              <div className="richtext richtext-center prose-headings:text-black prose-headings:font-geist prose-headings:font-[500] prose-headings:leading-none prose-headings:text-[4.5rem] prose-headings:tracking-[-0.225rem] lg:prose-headings:leading-[1.15] lg:prose-headings:text-[8rem] lg:prose-headings:tracking-[-0.4rem] w-[28.75rem] lg:w-auto [--hl-tracking:-0.03em] [--hl-dy:0.25rem] lg:[--hl-dy:-0.1rem]">
                <PortableText
                  value={data.tradersSayHeading}
                  components={measuredRichText}
                />
              </div>
            )}
          </div>
        )}

        {items.length > 0 && (
          <div className="flex flex-col items-center w-full gap-[3.125rem] lg:gap-[5rem]">
            <Carousel
              setApi={setApi}
              opts={{ loop: true, align: 'center' }}
              className="w-full"
            >
              <CarouselContent className="-ml-[1rem] lg:-ml-[3.25rem]">
                {items.map((item, i) => {
                  const active = i === current;
                  return (
                    <CarouselItem
                      key={`${item?.name ?? 'trader'}-${i}`}
                      className="basis-auto pl-[1rem] lg:pl-[3.25rem]"
                    >
                      <article className="flex flex-col items-start bg-white rounded-[0.75rem] overflow-clip w-[47.75rem] p-[2rem] gap-[1.875rem] lg:flex-row lg:items-center lg:w-[139.75rem] lg:gap-[6.5rem] lg:pl-[4rem] lg:pr-[2rem] lg:py-[2rem]">
                        <div className="flex flex-col justify-between w-full h-[29.625rem] p-[1.5rem] lg:w-[50.625rem] lg:h-[45rem] lg:px-0 lg:py-[1.5rem]">
                          <div className="flex flex-col items-start w-full gap-[3.25rem]">
                            {item?.chip && (
                              <span className="bg-[#6DA991]/[9%] font-blacker font-light text-black text-[2rem] leading-[1.5] px-[1.25rem] py-[1rem]">
                                {item.chip}
                              </span>
                            )}
                            {item?.text?.length > 0 && (
                              <div className="richtext richtext-start capitalize w-full prose-p:font-geist prose-p:font-[500] prose-p:text-[#103037] prose-p:leading-[1.4] prose-p:text-[2.5rem] prose-p:tracking-[-0.075rem] lg:prose-p:text-[3rem] lg:prose-p:tracking-[-0.09rem] prose-headings:font-geist prose-headings:font-[500] prose-headings:text-[#103037] prose-headings:leading-[1.4] prose-headings:text-[2.5rem] prose-headings:tracking-[-0.075rem] lg:prose-headings:text-[3rem] lg:prose-headings:tracking-[-0.09rem]">
                                <PortableText
                                  value={item.text}
                                  components={measuredRichText}
                                />
                              </div>
                            )}
                          </div>
                          {item?.name && (
                            <p
                              className={`m-0 w-full font-geist font-[600] text-[#103037] text-[2rem] leading-[1.11] tracking-[-0.06rem] lg:text-[2.5rem] lg:tracking-[-0.075rem] transition-opacity duration-500 ${
                                active ? 'opacity-100' : 'opacity-50'
                              }`}
                            >
                              {item.name}
                            </p>
                          )}
                        </div>

                        {/* The certificates are all 799 x 549 and the box the design
                            draws is wider, so `object-cover` took 10% off the height
                            and cut the certificate's header. Design review signed off
                            on the box growing to the asset's own ratio instead. */}
                        {item?.certificate && (
                          <div className="overflow-hidden relative shrink-0 bg-[#F4F4F4] border border-black/[4%] rounded-[0.5rem] w-full aspect-[799/549] lg:w-[76.625rem] lg:rounded-[0.875rem]">
                            <GroqImage
                              src={item.certificate}
                              alt={item?.name ?? ''}
                              quality={100}
                              style={{ width: '100%' }}
                              className="object-cover !w-full !h-full"
                            />
                          </div>
                        )}
                      </article>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
            </Carousel>

            <div className="flex items-center justify-center gap-[2.5rem] w-[47.75rem] lg:w-auto">
              <button
                type="button"
                aria-label="Previous"
                onClick={() => api?.scrollPrev()}
                className="flex justify-center items-center shrink-0 rounded-[1rem] bg-[#C2DED6] text-[#161616] w-[5rem] h-[5rem] transition-transform hover:-translate-x-[0.125rem]"
              >
                <Chevron className="w-[2.5rem] h-[2.5rem] rotate-180" />
              </button>
              <div className="flex-1 overflow-hidden h-[0.25rem] bg-[#6DA991]/20 lg:flex-none lg:w-[29.25rem]">
                <div
                  className="h-full bg-[#228E71] transition-[width] duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <button
                type="button"
                aria-label="Next"
                onClick={() => api?.scrollNext()}
                className="flex justify-center items-center shrink-0 rounded-[1rem] bg-[#003828] text-white w-[5rem] h-[5rem] transition-transform hover:translate-x-[0.125rem]"
              >
                <Chevron className="w-[2.5rem] h-[2.5rem]" />
              </button>
            </div>
          </div>
        )}

        {data?.tradersSayCTA && (
          <div className="hidden lg:flex justify-center mt-[1rem]">
            <ChevronCta
              data={data.tradersSayCTA}
              language={data?.settings?.language}
            />
          </div>
        )}
      </div>
    </section>
  );
}
